import { useEffect, useState } from "react";

import { Button } from "@blueprintjs/core";
import { useHotkeys } from "react-hotkeys-hook";

import { Box, Flex } from "@/components/layout/flexbox";
import { Media } from "@/components/layout/Media";
import { CoachCard } from "@/components/pieces/coaching/CoachCard";
import { CoachProfilesOverlay } from "@/components/pieces/coaching/CoachProfilesOverlay";
import { FadeInBox } from "@/components/pieces/FadeInBox";
import { HighlightText } from "@/components/pieces/HighlightText";
import { PageTitle } from "@/components/pieces/onboarding/PageTitle";
import { STANDARD_APP_MAX_WIDTH } from "@/css/constants";
import { ChooseCoachIntroProductQuery, useAgentProfilesQuery } from "@/graphql";
import { ListAgentProfile } from "@/types/coaches";
import { getBrowserTimezone } from "@/utils/datetime";

import { LoadingCoaches } from "./LoadingCoaches";

const FETCH_PAGE_SIZE = 24;
const DISPLAY_PAGE_SIZE = 3;

type Product = ChooseCoachIntroProductQuery["introProduct"];

interface SelectCoachProps {
  intakeFormToken?: string | null;
  loading: boolean;
  onSelectCoach: (coachId: string) => void;
  product: Product;
  includeCurrent: boolean;
  programComponentId: string;
}

export function SelectCoach({
  intakeFormToken,
  loading,
  onSelectCoach,
  product,
  includeCurrent,
  programComponentId,
}: SelectCoachProps) {
  const productSlug = product.slug;
  const [activeProfile, setActiveProfile] = useState<ListAgentProfile>();
  const [selectedCoachId, setSelectedCoachId] = useState<string>();
  const [currentPage, setCurrentPage] = useState(1);
  const [displayedProfileCount, setDisplayedProfileCount] =
    useState(DISPLAY_PAGE_SIZE);
  const [allProfiles, setAllProfiles] = useState<ListAgentProfile[]>([]);

  const { data, loading: loadingCoaches } = useAgentProfilesQuery({
    variables: {
      timeZone: getBrowserTimezone(),
      intakeFormToken,
      productSlug,
      includeCurrent,
      programComponentId,
      pageSize: FETCH_PAGE_SIZE,
      currentPage,
    },
    skip: !intakeFormToken,
  });

  const newProfiles = data?.agentProfiles.profiles ?? [];
  const totalRecords = data?.agentProfiles.totalRecords ?? 0;

  useEffect(() => {
    if (newProfiles.length > 0 && currentPage === 1) {
      setAllProfiles(newProfiles);
    } else if (newProfiles.length > 0 && currentPage > 1) {
      setAllProfiles((prev) => {
        const existingIds = new Set(prev.map((p) => p.id));
        const uniqueNewProfiles = newProfiles.filter(
          (p) => !existingIds.has(p.id),
        );
        return [...prev, ...uniqueNewProfiles];
      });
    }
  }, [newProfiles, currentPage]);

  const displayedProfiles = allProfiles.slice(0, displayedProfileCount);

  const handleLoadMore = async () => {
    const nextCount = displayedProfileCount + DISPLAY_PAGE_SIZE;
    setDisplayedProfileCount(nextCount);

    // If we're about to run out of profiles and there are more to fetch, get the next page
    if (nextCount >= allProfiles.length && allProfiles.length < totalRecords) {
      setCurrentPage(currentPage + 1);
    }
  };

  useHotkeys("escape", () => {
    setActiveProfile(undefined);
  });

  const headerBox = (
    <Box px={2}>
      Say hello to your matches for your{" "}
      <HighlightText key={productSlug}>{product.name}</HighlightText>
    </Box>
  );

  const subtitle = `We have great coaches who are perfect to help you.`;

  return (
    <Flex justifyContent="center">
      <Box width={[1, STANDARD_APP_MAX_WIDTH]}>
        <FadeInBox>
          {loading ? (
            <LoadingCoaches />
          ) : (
            <>
              <Flex justifyContent="center">
                <Box width={[1, 1, 710]}>
                  <PageTitle
                    style={{ textAlign: "center" }}
                    title={headerBox}
                    subtitle={subtitle}
                  />
                </Box>
              </Flex>
              <Flex flexWrap="wrap" px={[0, 3]} justifyContent="center">
                {displayedProfiles.map((profile) => (
                  <Box key={profile.id} px={12} width={[1, 1, 1 / 3]} my={12}>
                    <CoachCard
                      profile={profile}
                      onProfileSelect={(profile) =>
                        onSelectCoach(profile.user.id)
                      }
                      setActiveProfile={setActiveProfile}
                      active={activeProfile === profile}
                      disabled={!!selectedCoachId}
                      loading={selectedCoachId === profile?.user?.id}
                    />
                  </Box>
                ))}
              </Flex>
              {loadingCoaches || displayedProfileCount < totalRecords ? (
                <Flex justifyContent="center" mt={4}>
                  <Button
                    onClick={handleLoadMore}
                    large={true}
                    loading={loadingCoaches}
                    disabled={loadingCoaches}
                  >
                    {loadingCoaches ? "Loading..." : "More Options"}
                  </Button>
                </Flex>
              ) : null}
            </>
          )}
        </FadeInBox>
      </Box>

      <Media greaterThan="xs">
        <CoachProfilesOverlay
          coachProfiles={displayedProfiles}
          activeProfile={activeProfile}
          onClose={() => setActiveProfile(undefined)}
          onSelect={setSelectedCoachId}
          selectedCoachId={selectedCoachId}
        />
      </Media>
    </Flex>
  );
}
